import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Button, Spinner, Alert, Modal, Dropdown } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import ReactFlow, { Node, Edge, Background, Controls, MiniMap } from 'reactflow'
import 'reactflow/dist/base.css'
import 'reactflow/dist/style.css'
import { Activity, Eye, X, Palette, Filter } from 'lucide-react'
import { useAppDispatch } from '../../../store/hooks'
import { useGeneratePCAMutation, contentApi } from '../../../services/contentApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { useGetProjectQuery, useVectorizeProjectMutation } from '../../../services/projectsApi'
import { QueryContentResp } from '../../../types/models/content'
import {
  ChartContainer,
  ChartHeader,
  ChartTitle,
  FlowContainer,
  GenerateButton,
  LoadingContainer,
  ContentViewerModal,
  ContentGrid,
  ContentItem,
  ContentImage,
  ContentInfo,
  ButtonOverlay,
  Legend,
  LegendItem,
  LegendItemsContainer,
  LegendColor,
} from './styles'
import { DataPointNode, ExpandedDataPointNode, ClusterNode } from './components'

const nodeTypes = {
  dataPoint: DataPointNode,
  cluster: ({ data, id, selected }: any) => <ClusterNode data={data} id={id} selected={selected} />,
  expandedDataPoint: ExpandedDataPointNode,
}

interface DisplacementChartProps {
  isLoading?: boolean
  onStatsUpdate?: (stats: {
    contentData: any
    selectedNodes: Set<string>
    visualizationMode: string
    renderMode: string
    tagIdToNameAndColor: Record<string, { name: string; color: string }>
    onViewContent: () => void
    outlierReasons?: Map<number, string[]>
  }) => void
}

const DisplacementChart: React.FC<DisplacementChartProps> = ({
  isLoading = false,
  onStatsUpdate,
}) => {
  const { projectId } = useParams<{ projectId: string }>()
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })
  const [generatePCA, { isLoading: isGeneratingPCA }] = useGeneratePCAMutation()
  const [vectorizeProject, { isLoading: isVectorizing }] = useVectorizeProjectMutation()
  const [showChart, setShowChart] = useState(false)
  const [showMiniMap, setShowMiniMap] = useState(false)
  const [error, setError] = useState('')

  // Visualization mode state
  const [visualizationMode, setVisualizationMode] = useState<
    'default' | 'annotation' | 'source' | 'size' | 'outliers'
  >('default')

  // Lasso selection state
  const [lassoMode, setLassoMode] = useState(false)
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set())
  const [lassoPath, setLassoPath] = useState<{ x: number; y: number }[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null)
  const [showContentViewer, setShowContentViewer] = useState(false)

  // Tag visibility state for legend toggle functionality
  const [visibleTags, setVisibleTags] = useState<Set<string>>(new Set())

  // Legend expansion state
  const [expandedLegend, setExpandedLegend] = useState(false)

  // Storage keys for persistence
  const getStorageKey = (projectId: string) => `displacement-chart-${projectId}`
  const getLastProjectKey = () => 'displacement-chart-last-project'

  // Helper function to safely use localStorage with quota handling
  const safeLocalStorage = useMemo(
    () => ({
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value)
        } catch (error) {
          console.warn('localStorage quota exceeded, clearing old displacement chart data')
          // Clear old displacement chart data
          Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.startsWith('displacement-chart-') && storageKey !== key) {
              localStorage.removeItem(storageKey)
            }
          })
          // Try again after cleanup
          try {
            localStorage.setItem(key, value)
          } catch (retryError) {
            console.error('Failed to store displacement chart data even after cleanup:', retryError)
          }
        }
      },
      getItem: (key: string) => {
        try {
          return localStorage.getItem(key)
        } catch (error) {
          console.warn('Failed to read from localStorage:', error)
          return null
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key)
        } catch (error) {
          console.warn('Failed to remove from localStorage:', error)
        }
      },
    }),
    []
  )

  // Handle project switching - clear old data and load new data
  useEffect(() => {
    if (!projectId) return

    // Check if project has changed
    const lastProject = safeLocalStorage.getItem(getLastProjectKey())
    if (lastProject && lastProject !== projectId) {
      // Clear old project data
      if (lastProject) {
        safeLocalStorage.removeItem(getStorageKey(lastProject))
      }
      // Reset view initialization for new project
      setHasInitializedView(false)
    }

    // Update last project
    safeLocalStorage.setItem(getLastProjectKey(), projectId)

    // Check for persisted data for current project
    const persistedData = safeLocalStorage.getItem(getStorageKey(projectId))
    if (persistedData) {
      try {
        setShowChart(true)
      } catch (error) {
        // Invalid data, remove it
        safeLocalStorage.removeItem(getStorageKey(projectId))
      }
    }
  }, [projectId])

  // Debug selectedNodes changes
  React.useEffect(() => {
    // Force re-render when selectedNodes changes
  }, [selectedNodes])

  // Performance and rendering state
  const [renderMode, setRenderMode] = useState<'full' | 'sample' | 'viewport' | 'clustered'>(
    'clustered'
  )
  const [sampleSize, setSampleSize] = useState(2000)

  const CLUSTER_DISTANCE = 30
  const MIN_CLUSTER_SIZE = 3

  // Cluster expansion state
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
  const [isClusterExpanded, setIsClusterExpanded] = useState(false)

  // Zoom-based dynamic spacing state
  const [currentZoom, setCurrentZoom] = useState(1)

  // Custom pagination hook for fetching all content pages
  // Custom pagination hook for fetching all content pages
  const [allContentData, setAllContentData] = useState<QueryContentResp | null>(null)
  const [isLoadingAllContent, setIsLoadingAllContent] = useState(false)
  const [contentFetchError, setContentFetchError] = useState<string | null>(null)

  const dispatch = useAppDispatch()

  // Function to handle cluster expansion
  const handleClusterClick = useCallback(
    (clusterId: string) => {
      const newExpandedClusters = new Set(expandedClusters)

      if (expandedClusters.has(clusterId)) {
        // Collapse the cluster
        newExpandedClusters.delete(clusterId)
      } else {
        // Expand the cluster
        newExpandedClusters.add(clusterId)
      }

      setExpandedClusters(newExpandedClusters)
      setIsClusterExpanded(newExpandedClusters.size > 0)
    },
    [expandedClusters]
  )

  // Function to reset all cluster expansions
  const resetClusterExpansions = useCallback(() => {
    setExpandedClusters(new Set())
    setIsClusterExpanded(false)
  }, [])

  // Calculate zoom-adjusted positions - spreads nodes out more as zoom increases
  const calculateZoomAdjustedPositions = useCallback((nodes: any[], zoom: number) => {
    if (zoom <= 1.1 || nodes.length === 0) return nodes // No adjustment needed at low zoom levels

    // Calculate the center point of all nodes
    const centerX = nodes.reduce((sum, node) => sum + node.position.x, 0) / nodes.length
    const centerY = nodes.reduce((sum, node) => sum + node.position.y, 0) / nodes.length

    // Much more aggressive spacing multiplier for fine-grained separation
    // At zoom 2x: 2.5x spacing, at zoom 4x: 5x spacing, at zoom 8x: 10x spacing
    const spacingMultiplier = Math.pow(zoom, 1.8)

    return nodes.map((node) => {
      // Calculate distance from center
      const deltaX = node.position.x - centerX
      const deltaY = node.position.y - centerY

      // Apply spacing multiplier with reduced damping for more aggressive separation
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      const dampingFactor = Math.min(1, distance / 10) // Less damping for more separation

      const adjustedMultiplier = 1 + (spacingMultiplier - 1) * dampingFactor

      const newX = centerX + deltaX * adjustedMultiplier
      const newY = centerY + deltaY * adjustedMultiplier

      return {
        ...node,
        position: {
          x: newX,
          y: newY,
        },
      }
    })
  }, [])

  // Function to fetch all pages of content with the working query structure
  const fetchAllContentPages = useCallback(async () => {
    if (!projectId) return

    setIsLoadingAllContent(true)
    setContentFetchError(null)

    try {
      const allContent: any[] = []
      let currentPage = 1
      let hasMorePages = true
      const pageSize = 1000

      // Test if pagination is 0-based by trying page 0 first
      try {
        const testResponse = await dispatch(
          contentApi.endpoints.queryContent.initiate({
            filters: [
              {
                key: 'projectid',
                value: projectId,
              },
              {
                key: 'vectorized',
                value: 'true',
              },
            ],
            operator: 'and',
            limit: pageSize,
            page: 0,
          })
        ).unwrap()

        if (testResponse.content && testResponse.content.length > 0) {
          currentPage = 0
        }
      } catch (error) {
        // Assuming 1-based pagination
      }

      while (hasMorePages) {
        // Use the working query structure that was successful before
        const response = await dispatch(
          contentApi.endpoints.queryContent.initiate({
            filters: [
              {
                key: 'projectid',
                value: projectId,
              },
              {
                key: 'vectorized',
                value: 'true',
              },
            ],
            operator: 'and',
            limit: pageSize,
            page: currentPage,
          })
        ).unwrap()

        if (response.content && response.content.length > 0) {
          allContent.push(...response.content)

          // Check if we got a full page - if not, we're done
          // Also check if we've reached the expected total count from the API
          if (response.content.length < pageSize || allContent.length >= response.count) {
            hasMorePages = false
          } else {
            currentPage++
          }
        } else {
          hasMorePages = false
        }

        // Safety check to prevent infinite loops
        if (currentPage > 100) {
          break
        }
      }

      // Auto-adjust render mode based on data size
      if (allContent.length > 5000) {
        setRenderMode('clustered') // Use clustering for large datasets
      } else if (allContent.length > 2000) {
        setRenderMode('sample')
        setSampleSize(1500)
      } else {
        setRenderMode('full')
      }

      setAllContentData({
        content: allContent,
        count: allContent.length,
        page: currentPage - 1,
      })
    } catch (error) {
      console.error('Error fetching all content pages:', error)
      setContentFetchError('Failed to fetch content data')
    } finally {
      setIsLoadingAllContent(false)
    }
  }, [projectId, dispatch])

  // Intelligent sampling function that preserves distribution
  const sampleContentData = useCallback((contentArray: any[], targetSize: number) => {
    if (contentArray.length <= targetSize) return contentArray

    // Strategy 1: Stratified sampling by annotation status to preserve distribution
    const annotated = contentArray.filter((item) => item.annotation && item.annotation.length > 0)
    const unannotated = contentArray.filter(
      (item) => !item.annotation || item.annotation.length === 0
    )

    const annotatedRatio = annotated.length / contentArray.length
    const targetAnnotated = Math.floor(targetSize * annotatedRatio)
    const targetUnannotated = targetSize - targetAnnotated

    // Random sampling within each group
    const shuffleArray = (array: any[]) => {
      const shuffled = [...array]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      return shuffled
    }

    const sampledAnnotated = shuffleArray(annotated).slice(0, targetAnnotated)
    const sampledUnannotated = shuffleArray(unannotated).slice(0, targetUnannotated)

    return [...sampledAnnotated, ...sampledUnannotated]
  }, [])

  // Get processed content data based on render mode
  const processedContentData = useMemo(() => {
    if (!allContentData?.content) return null

    const contentArray = allContentData.content as any[]

    switch (renderMode) {
      case 'full':
        return { ...allContentData, content: contentArray }

      case 'sample':
        const sampledContent = sampleContentData(contentArray, sampleSize)
        return { ...allContentData, content: sampledContent }

      case 'clustered':
        // Return full data for clustering - clustering will handle the performance optimization
        return { ...allContentData, content: contentArray }

      case 'viewport':
        // For now, fall back to sampling. Viewport culling would require more complex implementation
        return {
          ...allContentData,
          content: sampleContentData(contentArray, 1000),
        }

      default:
        return allContentData
    }
  }, [allContentData, renderMode, sampleSize, sampleContentData])

  // Fetch all content when component mounts or project changes
  useEffect(() => {
    if (projectId && showChart) {
      fetchAllContentPages()
    }
  }, [projectId, showChart, fetchAllContentPages])

  // Store content data for persistence (with size limiting)
  React.useEffect(() => {
    if (allContentData && projectId && showChart) {
      // Limit stored data size by removing large fields like b64_image
      const compressedData = {
        ...allContentData,
        content: allContentData.content?.map((item: any) => ({
          ...item,
          b64_image: undefined, // Remove large base64 images
          annotation: item.annotation?.map((ann: any) => ({
            ...ann,
            b64_image: undefined, // Remove base64 from annotations too
          })),
        })),
      }
      safeLocalStorage.setItem(getStorageKey(projectId), JSON.stringify(compressedData))
    }
  }, [allContentData, projectId, safeLocalStorage, showChart])

  // Determine which content data to use
  const activeContentData = React.useMemo(() => {
    if (!projectId) return null

    // Use fresh data if available
    return allContentData
  }, [allContentData, projectId])

  // Get selected content items
  const selectedContentItems = useMemo(() => {
    if (!processedContentData?.content || selectedNodes.size === 0) return []

    const contentArray = processedContentData.content
    const selectedItems: any[] = []

    selectedNodes.forEach((nodeId) => {
      const nodeIndex = parseInt(nodeId.replace('node-', ''))
      const contentItem = contentArray[nodeIndex]
      if (contentItem) {
        selectedItems.push(contentItem)
      }
    })

    return selectedItems
  }, [processedContentData, selectedNodes])

  // Fetch tags for the project/dataset
  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectId!,
      dataset_id: project?.datasetid || '',
    },
    { skip: !projectId || !project?.datasetid }
  )

  // Create a mapping from tag ID to tag name and color
  const tagIdToNameAndColor = useMemo(() => {
    if (!tagsData?.tags) return {}

    const mapping: Record<string, { name: string; color: string }> = {}
    tagsData.tags.forEach((tag) => {
      // Convert color number to hex color
      const hexColor = `#${tag.color.toString(16).padStart(6, '0')}`
      mapping[tag.id] = {
        name: tag.name,
        color: hexColor,
      }
    })
    return mapping
  }, [tagsData])

  // Helper function to get the most dominant tag from annotations
  const getMostDominantTag = useCallback((annotations: any[] = []) => {
    if (!annotations || annotations.length === 0) return null

    // Count tag occurrences across all annotations
    const tagCounts: Record<string, number> = {}
    annotations.forEach((ann) => {
      if (ann.tagids && Array.isArray(ann.tagids)) {
        ann.tagids.forEach((tagId: string) => {
          tagCounts[tagId] = (tagCounts[tagId] || 0) + 1
        })
      }
    })

    // Find the most frequent tag
    let mostFrequentTag = null
    let maxCount = 0
    Object.entries(tagCounts).forEach(([tagId, count]) => {
      if (count > maxCount) {
        maxCount = count
        mostFrequentTag = tagId
      }
    })

    return mostFrequentTag
  }, [])

  // Initialize visible tags when tag data changes or when switching to annotation mode
  useEffect(() => {
    if (visualizationMode === 'annotation') {
      // Show all tags by default
      if (tagsData?.tags) {
        const allTagIds = new Set(tagsData.tags.map((tag) => tag.id))
        allTagIds.add('no-tags')
        setVisibleTags(allTagIds)
      } else {
        setVisibleTags(new Set(['no-tags']))
      }
    } else {
      // Clear visible tags when not in annotation mode
      setVisibleTags(new Set())
    }
  }, [tagsData, visualizationMode])

  // Helper function to get tag names from tag IDs
  const getTagNames = useCallback(
    (tagIds: string[] = []) => {
      return tagIds.map((id) => tagIdToNameAndColor[id]?.name || id).join(', ')
    },
    [tagIdToNameAndColor]
  )

  // Helper function to render tag badges with colors
  const renderTagBadges = useCallback(
    (tagIds: string[] = []) => {
      if (!tagIds || tagIds.length === 0) return null

      return tagIds
        .map((id) => {
          const tagInfo = tagIdToNameAndColor[id]
          if (!tagInfo) return null

          return (
            <span
              key={id}
              style={{
                display: 'inline-block',
                backgroundColor: tagInfo.color,
                color: '#ffffff',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                marginRight: '0.25rem',
                marginBottom: '0.25rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              {tagInfo.name}
            </span>
          )
        })
        .filter(Boolean)
    },
    [tagIdToNameAndColor]
  )

  // Helper function to get all tags from an item's annotations
  const getAllTagsFromItem = useCallback((item: any): string[] => {
    if (!item.annotation || !Array.isArray(item.annotation)) return []

    const allTags: string[] = []
    item.annotation.forEach((ann: any) => {
      if (ann.tagids && Array.isArray(ann.tagids)) {
        allTags.push(...ann.tagids)
      }
    })
    return [...new Set(allTags)] // Remove duplicates
  }, [])

  // Helper function to check if an item should be visible based on tag visibility settings
  const isItemVisibleByTag = useCallback(
    (item: any, index?: number): boolean => {
      if (visualizationMode !== 'annotation') {
        return true
      }

      if (visibleTags.size === 0) {
        return true // Show all when no visibility rules are set
      }

      // Check if item has annotations
      if (!item.annotation || item.annotation.length === 0) {
        // No annotations - check if "no-tags" is visible
        const visible = visibleTags.has('no-tags')
        return visible
      }

      // Item has annotations - check if its dominant tag is visible
      const dominantTagId = getMostDominantTag(item.annotation)

      if (dominantTagId) {
        // Item has a dominant tag - show only if that tag is visible
        const visible = visibleTags.has(dominantTagId)
        return visible
      }

      // If no dominant tag found, fall back to checking "no-tags" visibility
      // (for items with annotations but no recognizable tags)
      const fallbackVisible = visibleTags.has('no-tags')
      return fallbackVisible
    },
    [visualizationMode, visibleTags, getMostDominantTag]
  )

  // Helper function to get unique sources from the dataset
  const getUniqueSources = useMemo(() => {
    if (!activeContentData?.content) return []

    const sources = new Set<string>()
    activeContentData.content.forEach((item: any) => {
      if (item.metadata?.datasource) {
        sources.add(item.metadata.datasource)
      }
    })
    return Array.from(sources).sort()
  }, [activeContentData])

  // Outlier detection functions with detailed reasons
  const { detectOutliers, outlierReasons } = useMemo(() => {
    if (!activeContentData?.content)
      return {
        detectOutliers: new Set<number>(),
        outlierReasons: new Map<number, string[]>(),
      }

    const contentArray = activeContentData.content as any[]
    const outliers = new Set<number>()
    const reasons = new Map<number, string[]>()

    // Helper function to add reason
    const addReason = (index: number, reason: string) => {
      if (!reasons.has(index)) {
        reasons.set(index, [])
      }
      reasons.get(index)?.push(reason)
      outliers.add(index)
    }

    // Method 1: Vector distance outliers (isolation in PCA space)
    const vectors = contentArray
      .map((item: any, index: number) => ({ vector: item.vector, index }))
      .filter((item) => item.vector && item.vector.length >= 2)

    if (vectors.length > 10) {
      // Calculate centroid
      const centroidX = vectors.reduce((sum, item) => sum + item.vector[0], 0) / vectors.length
      const centroidY = vectors.reduce((sum, item) => sum + item.vector[1], 0) / vectors.length

      // Calculate distances from centroid
      const distances = vectors.map((item) => ({
        index: item.index,
        distance: Math.sqrt(
          Math.pow(item.vector[0] - centroidX, 2) + Math.pow(item.vector[1] - centroidY, 2)
        ),
      }))

      // Find outliers using IQR method
      const sortedDistances = distances.map((d) => d.distance).sort((a, b) => a - b)
      const q1 = sortedDistances[Math.floor(sortedDistances.length * 0.25)]
      const q3 = sortedDistances[Math.floor(sortedDistances.length * 0.75)]
      const iqr = q3 - q1
      const outlierThreshold = q3 + 1.5 * iqr

      distances.forEach((item) => {
        if (item.distance > outlierThreshold) {
          const percentile = (
            (sortedDistances.filter((d) => d < item.distance).length / sortedDistances.length) *
            100
          ).toFixed(1)
          addReason(
            item.index,
            `Isolated in PCA space (${percentile}th percentile distance from center)`
          )
        }
      })
    }

    // Method 2: Size outliers (unusual image dimensions)
    const imageSizes = contentArray
      .map((item: any, index: number) => ({
        index,
        width: item.width,
        height: item.height,
        area: item.width && item.height ? item.width * item.height : null,
      }))
      .filter((item) => item.area !== null)

    if (imageSizes.length > 10) {
      const areas = imageSizes.map((item) => item?.area || 0).sort((a, b) => a - b)
      const q1 = areas[Math.floor(areas.length * 0.25)]
      const q3 = areas[Math.floor(areas.length * 0.75)]
      const iqr = q3 - q1
      const lowerBound = q1 - 1.5 * iqr
      const upperBound = q3 + 1.5 * iqr
      const median = areas[Math.floor(areas.length * 0.5)]

      imageSizes.forEach((item) => {
        if ((item.area ?? 0) < lowerBound) {
          const megapixels = ((item.area ?? 0) / 1000000).toFixed(2)
          const medianMp = (median / 1000000).toFixed(2)
          addReason(
            item.index,
            `Unusually small image (${item.width}×${item.height}, ${megapixels}MP vs median ${medianMp}MP)`
          )
        } else if ((item.area ?? 0) > upperBound) {
          const megapixels = ((item.area ?? 0) / 1000000).toFixed(2)
          const medianMp = (median / 1000000).toFixed(2)
          addReason(
            item.index,
            `Unusually large image (${item.width}×${item.height}, ${megapixels}MP vs median ${medianMp}MP)`
          )
        }
      })
    }

    // Method 3: Annotation density outliers
    const annotationCounts = contentArray.map((item: any, index: number) => ({
      index,
      count: item.annotation ? item.annotation.length : 0,
    }))

    if (annotationCounts.length > 10) {
      const counts = annotationCounts.map((item) => item.count).sort((a, b) => a - b)
      const q3 = counts[Math.floor(counts.length * 0.75)]
      const median = counts[Math.floor(counts.length * 0.5)]
      const upperBound = q3 + 1.5 * (q3 - counts[Math.floor(counts.length * 0.25)])

      // Only flag items with unusually high annotation counts as outliers
      annotationCounts.forEach((item) => {
        if (item.count > upperBound && item.count > 5) {
          // At least 5 annotations to be considered
          addReason(
            item.index,
            `High annotation density (${item.count} annotations vs median ${median})`
          )
        }
      })
    }

    // Method 4: Tag diversity outliers (items with unusually many different tags)
    const tagDiversities = contentArray.map((item: any, index: number) => {
      const uniqueTags = new Set<string>()
      if (item.annotation && Array.isArray(item.annotation)) {
        item.annotation.forEach((ann: any) => {
          if (ann.tagids && Array.isArray(ann.tagids)) {
            ann.tagids.forEach((tagId: string) => uniqueTags.add(tagId))
          }
        })
      }
      return { index, diversity: uniqueTags.size }
    })

    if (tagDiversities.length > 10) {
      const diversities = tagDiversities.map((item) => item.diversity).sort((a, b) => a - b)
      const q3 = diversities[Math.floor(diversities.length * 0.75)]
      const median = diversities[Math.floor(diversities.length * 0.5)]

      // Flag items with significantly more tag diversity than median
      tagDiversities.forEach((item) => {
        if (item.diversity > q3 && item.diversity > median * 2 && item.diversity >= 3) {
          addReason(
            item.index,
            `High tag diversity (${item.diversity} unique tags vs median ${median})`
          )
        }
      })
    }

    return { detectOutliers: outliers, outlierReasons: reasons }
  }, [activeContentData])

  // Helper function to get source color
  const getSourceColor = useCallback(
    (source: string) => {
      const sourceColors = [
        '#114ee0',
        '#28a745',
        '#dc3545',
        '#ffc107',
        '#17a2b8',
        '#6f42c1',
        '#fd7e14',
        '#20c997',
      ]
      const sourceIndex = getUniqueSources.indexOf(source)
      return sourceIndex >= 0 ? sourceColors[sourceIndex % sourceColors.length] : '#6c757d'
    },
    [getUniqueSources]
  )

  // Helper function to get node color based on visualization mode
  const getNodeColor = useCallback(
    (item: any, index: number) => {
      switch (visualizationMode) {
        case 'annotation':
          if (item.annotation && item.annotation.length > 0) {
            const dominantTagId = getMostDominantTag(item.annotation)
            if (dominantTagId && tagIdToNameAndColor[dominantTagId]) {
              return tagIdToNameAndColor[dominantTagId].color
            }
            return '#28a745' // Green for annotated but no tag info
          }
          return '#6c757d' // Gray for unannotated

        case 'source':
          // Color by actual data source from metadata
          if (item.metadata?.datasource) {
            return getSourceColor(item.metadata.datasource)
          }
          return '#6c757d' // Gray for unknown/missing source

        case 'size':
          // Color by image dimensions (max dimension)
          if (item.width && item.height) {
            const maxDimension = Math.max(item.width, item.height)
            if (maxDimension >= 2048) return '#e83e8c' // Pink for huge (2K+)
            if (maxDimension >= 1024) return '#6f42c1' // Purple for very large (1K-2K)
            if (maxDimension >= 512) return '#17a2b8' // Teal for large (512-1K)
            if (maxDimension >= 256) return '#007bff' // Blue for medium (256-512)
            if (maxDimension >= 128) return '#20c997' // Mint green for small (128-256)
            return '#fd7e14' // Orange for tiny (<128)
          }
          return '#6c757d' // Gray for unknown size

        case 'outliers':
          // Color by outlier status
          if (detectOutliers.has(index)) {
            return '#dc3545' // Red for outliers
          }
          return '#28a745' // Green for normal items

        case 'default':
        default:
          // Original logic: green for annotated, blue for unannotated
          return item.annotation && item.annotation.length > 0 ? '#28a745' : '#114ee0'
      }
    },
    [visualizationMode, getMostDominantTag, tagIdToNameAndColor, getSourceColor, detectOutliers]
  )

  // Helper function to get node label based on visualization mode
  const getNodeLabel = useCallback(
    (item: any, index: number) => {
      const baseName = item.name || `Point ${index + 1}`

      switch (visualizationMode) {
        case 'annotation':
          if (item.annotation && item.annotation.length > 0) {
            const dominantTagId = getMostDominantTag(item.annotation)
            const tagInfo = dominantTagId ? tagIdToNameAndColor[dominantTagId] : null
            return tagInfo ? `${baseName}\n(${tagInfo.name})` : baseName
          }
          return `${baseName}\n(No annotations)`

        case 'source':
          return item.metadata?.datasource ? `${baseName}\n(${item.metadata.datasource})` : baseName

        case 'size':
          if (item.width && item.height) {
            const maxDimension = Math.max(item.width, item.height)
            let sizeCategory = ''
            if (maxDimension >= 2048) sizeCategory = 'Huge'
            else if (maxDimension >= 1024) sizeCategory = 'Very Large'
            else if (maxDimension >= 512) sizeCategory = 'Large'
            else if (maxDimension >= 256) sizeCategory = 'Medium'
            else if (maxDimension >= 128) sizeCategory = 'Small'
            else sizeCategory = 'Tiny'
            return `${baseName}\n(${item.width}×${item.height} - ${sizeCategory})`
          }
          return baseName

        case 'outliers':
          if (detectOutliers.has(index)) {
            const itemReasons = outlierReasons.get(index) || ['Statistical outlier']
            const reasonText = itemReasons.join('\n• ')
            return `${baseName}\n\n🚨 OUTLIER DETECTED:\n• ${reasonText}`
          }
          return `${baseName}\n✅ Normal item`

        case 'default':
        default:
          return baseName
      }
    },
    [
      visualizationMode,
      getMostDominantTag,
      tagIdToNameAndColor,
      detectOutliers,
      outlierReasons,
      activeContentData,
    ]
  )

  // Clustering algorithm - groups nearby nodes of the same color
  const clusterNodes = useCallback(
    (items: any[]) => {
      if (!items || items.length === 0) return { clusters: [], individualNodes: [] }

      // Always cluster the full dataset to maintain PCA accuracy and stable positions

      // First, create all individual nodes with positions
      const allNodes = items.map((item: any, index: number) => ({
        id: `node-${index}`,
        item,
        index,
        color: getNodeColor(item, index),
        position: null as { x: number; y: number } | null,
        clustered: false,
      }))

      // Calculate positions for all nodes
      const vectors = items.map((item: any) => item.vector)
      const xValues = vectors.map((v: number[]) => v[0])
      const yValues = vectors.map((v: number[]) => v[1])
      const xMin = Math.min(...xValues)
      const xMax = Math.max(...xValues)
      const yMin = Math.min(...yValues)
      const yMax = Math.max(...yValues)

      // Use the same dimensions as the main chart for consistency
      const CHART_WIDTH = 1200
      const CHART_HEIGHT = 800
      const CHART_MARGIN = 100

      allNodes.forEach((node) => {
        const normalizedX = (node.item.vector[0] - xMin) / (xMax - xMin || 1)
        const normalizedY = (node.item.vector[1] - yMin) / (yMax - yMin || 1)
        node.position = {
          x: normalizedX * CHART_WIDTH + CHART_MARGIN,
          y: normalizedY * CHART_HEIGHT + CHART_MARGIN,
        }
        // Create a copy of the item with calculated positions instead of modifying the original
        node.item = {
          ...node.item,
          calculatedX: node.position.x,
          calculatedY: node.position.y,
        }
      })

      // Group nodes by color first
      const colorGroups: Record<string, typeof allNodes> = {}
      allNodes.forEach((node) => {
        if (!colorGroups[node.color]) {
          colorGroups[node.color] = []
        }
        colorGroups[node.color].push(node)
      })

      const clusters: any[] = []
      const individualNodes: any[] = []

      // Process each color group separately
      Object.entries(colorGroups).forEach(([color, nodes], index: number) => {
        const unclustered = [...nodes]

        while (unclustered.length > 0) {
          const seed = unclustered.shift() || null
          const cluster = [seed]

          // Find nearby nodes of the same color
          for (let i = unclustered.length - 1; i >= 0; i--) {
            const candidate = unclustered[i]
            const distance = Math.sqrt(
              Math.pow(seed?.position?.x || 0 - (candidate?.position?.x || 0), 2) +
                Math.pow(seed?.position?.y || 0 - (candidate?.position?.y || 0), 2)
            )

            if (distance <= CLUSTER_DISTANCE) {
              cluster.push(candidate)
              unclustered.splice(i, 1)
            }
          }

          // Create cluster or individual node
          if (cluster.length >= MIN_CLUSTER_SIZE) {
            // Calculate cluster center
            const centerX =
              cluster.reduce((sum, node) => sum + (node?.position?.x || 0), 0) / cluster.length
            const centerY =
              cluster.reduce((sum, node) => sum + (node?.position?.y || 0), 0) / cluster.length

            const clusterId = `cluster-${clusters.length}`
            clusters.push({
              id: clusterId,
              type: 'cluster',
              position: { x: centerX, y: centerY },
              data: {
                color,
                count: cluster.length,
                items: cluster.map((n: any) => n?.item),
                nodeIds: cluster.map((n: any) => n?.id || `node-${index}`),
              },
              draggable: false,
              selectable: true,
              selected: selectedNodes.has(clusterId),
            })
          } else {
            // Add as individual nodes
            cluster.forEach((node: any, index: number) => {
              individualNodes.push({
                id: node?.id || `node-${index}`,
                type: 'dataPoint',
                position: node?.position || { x: 0, y: 0 },
                data: {
                  label: getNodeLabel(node?.item, node?.index || index),
                  color: node?.color,
                  content: node?.item,
                  selected: selectedNodes.has(node?.id || `node-${index}`),
                },
                draggable: false,
                selectable: true,
                selected: selectedNodes.has(node.id),
              })
            })
          }
        }
      })

      return { clusters, individualNodes }
    },
    [getNodeColor, getNodeLabel, selectedNodes, isItemVisibleByTag]
  )

  // Function to expand a cluster - instead of moving nodes, we'll highlight them in place
  const expandClusterNodes = useCallback(
    (cluster: any) => {
      const { items, nodeIds } = cluster.data

      // Create individual nodes at their actual PCA positions with visual enhancements
      return items.map((item: any, index: number) => {
        // We need to recalculate the actual position for each item using the global scaling
        // This requires access to all filtered items to get the proper min/max bounds
        const nodeId = nodeIds[index] || `expanded-${cluster.id}-${index}`
        const isSelected = selectedNodes.has(nodeId)

        return {
          id: nodeId,
          type: 'expandedDataPoint', // Use a special type for expanded nodes
          position: {
            x: item.calculatedX || cluster.position.x, // Fallback to cluster center if no calculated position
            y: item.calculatedY || cluster.position.y,
          },
          data: {
            label: getNodeLabel(item, index),
            color: cluster.data.color,
            content: item,
            selected: isSelected,
            isExpanded: true,
            parentClusterId: cluster.id,
            clusterCenter: cluster.position,
            // Enhanced visual properties
            glowColor: cluster.data.color,
            showConnection: true,
          },
          draggable: false,
          selectable: true,
          selected: isSelected,
        }
      })
    },
    [selectedNodes, getNodeLabel]
  )

  const handleGeneratePCA = async () => {
    if (!projectId) return

    try {
      setError('')

      // Clear existing data when regenerating PCA
      if (activeContentData) {
        safeLocalStorage.removeItem(getStorageKey(projectId))
      }

      // Reset view initialization for new PCA
      setHasInitializedView(false)

      await generatePCA({ projectId }).unwrap()
      setShowChart(true)

      // Refetch content data after PCA generation
      setTimeout(() => {
        fetchAllContentPages()
      }, 1500) // Give the backend time to process
    } catch (err: any) {
      // More detailed error handling
      let errorMessage = 'Failed to generate PCA'

      if (err?.status === 400) {
        errorMessage = 'PCA generation failed - please check your data has valid vectors'
      } else if (err?.status === 409) {
        errorMessage = 'PCA already exists and is up to date'
      } else if (err?.status === 500) {
        errorMessage = 'Server error during PCA generation - please try again'
      } else if (err?.data?.message) {
        errorMessage = err.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)

      // If it's a "conflict" error (PCA already exists), still show the chart
      if (err?.status === 409 && activeContentData) {
        setShowChart(true)
      }
    }
  }

  const handleVectorizeProject = async () => {
    if (!projectId) return

    try {
      setError('')

      await vectorizeProject({ projectId }).unwrap()

      // Show success message or handle completion
      console.log('Project vectorization completed successfully')

      // Optionally refetch content data after vectorization
      setTimeout(() => {
        fetchAllContentPages()
      }, 1000) // Give the backend time to process
    } catch (err: any) {
      // Error handling
      let errorMessage = 'Failed to vectorize project'

      if (err?.status === 400) {
        errorMessage = 'Vectorization failed - please check your project data'
      } else if (err?.status === 409) {
        errorMessage = 'Project is already vectorized'
      } else if (err?.status === 500) {
        errorMessage = 'Server error during vectorization - please try again'
      } else if (err?.data?.message) {
        errorMessage = err.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    }
  }

  // Convert content data to nodes for ReactFlow (now using processed data)
  const { nodes, edges } = useMemo(() => {
    if (!processedContentData?.content) {
      return { nodes: [], edges: [] }
    }

    // Access the content array directly
    const contentArray = processedContentData.content as any[]
    const filteredItems = contentArray.filter((item: any) => item.vector && item.vector.length >= 2)

    // Use clustering if in clustered mode
    if (renderMode === 'clustered') {
      const { clusters, individualNodes } = clusterNodes(filteredItems)

      // Handle cluster expansion - replace expanded clusters with their individual nodes
      const finalNodes: any[] = []

      clusters.forEach((cluster) => {
        if (expandedClusters.has(cluster.id)) {
          // Cluster is expanded - add individual nodes arranged around cluster center
          const expandedNodes = expandClusterNodes(cluster)
          finalNodes.push(...expandedNodes)

          // Add the cluster node but make it transparent/hidden when expanded
          finalNodes.push({
            ...cluster,
            data: {
              ...cluster.data,
              isExpanded: true,
              isHidden: true, // Mark as hidden so it doesn't interfere with expanded nodes
            },
            selected: selectedNodes.has(cluster.id),
          })
        } else {
          // Cluster is collapsed - add the cluster node
          finalNodes.push({
            ...cluster,
            selected: selectedNodes.has(cluster.id),
          })
        }
      })

      // Add individual nodes (non-clustered items)
      finalNodes.push(...individualNodes)

      return { nodes: finalNodes, edges: [] }
    }

    // Original non-clustered logic
    // Always use full dataset for accurate PCA positioning

    // Calculate bounds for proper scaling using full dataset
    const vectors = filteredItems.map((item: any) => item.vector)
    const xValues = vectors.map((v: number[]) => v[0])
    const yValues = vectors.map((v: number[]) => v[1])
    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)
    const yMin = Math.min(...yValues)
    const yMax = Math.max(...yValues)

    // Use a large chart area to better utilize the available ReactFlow space
    const CHART_WIDTH = 1200
    const CHART_HEIGHT = 800
    const CHART_MARGIN = 100

    const flowNodes: Node[] = filteredItems.map((item: any, index: number) => {
      // Normalize vectors to 0-1 range, then scale to chart dimensions
      const normalizedX = (item.vector[0] - xMin) / (xMax - xMin || 1)
      const normalizedY = (item.vector[1] - yMin) / (yMax - yMin || 1)

      const nodeId = `node-${index}`
      const isSelected = selectedNodes.has(nodeId)

      const node = {
        id: nodeId,
        type: 'dataPoint',
        position: {
          x: normalizedX * CHART_WIDTH + CHART_MARGIN,
          y: normalizedY * CHART_HEIGHT + CHART_MARGIN,
        },
        data: {
          label: getNodeLabel(item, index),
          color: getNodeColor(item, index),
          content: item,
          selected: isSelected,
        },
        draggable: false,
        selectable: true,
        selected: isSelected,
      }

      return node
    })

    return { nodes: flowNodes, edges: [] }
  }, [
    processedContentData,
    selectedNodes,
    getNodeLabel,
    getNodeColor,
    renderMode,
    clusterNodes,
    expandedClusters,
    expandClusterNodes,
  ])

  // Filter nodes for rendering while maintaining accurate positions
  const filteredNodesForRendering = useMemo(() => {
    if (visualizationMode !== 'annotation' || visibleTags.size === 0) {
      // No filtering needed - show all nodes (this ensures zoom spacing works for all modes)
      return nodes
    }

    // Filter nodes based on tag visibility, but keep accurate positions
    const filtered = nodes.filter((node) => {
      if (node.type === 'cluster') {
        // For clusters, check if any items in the cluster should be visible
        const clusterData = node.data
        if (clusterData.items && Array.isArray(clusterData.items)) {
          return clusterData.items.some((item: any) => isItemVisibleByTag(item))
        }
        return true
      } else if (node.type === 'expandedDataPoint') {
        // For expanded nodes, check the content
        return isItemVisibleByTag(node.data.content)
      } else {
        // For regular nodes, check the content
        return isItemVisibleByTag(node.data.content)
      }
    })

    return filtered
  }, [nodes, visualizationMode, visibleTags, isItemVisibleByTag])

  // Apply zoom-based spacing to improve separation when zoomed in
  const zoomAdjustedNodes = useMemo(() => {
    return calculateZoomAdjustedPositions(filteredNodesForRendering, currentZoom)
  }, [filteredNodesForRendering, currentZoom, calculateZoomAdjustedPositions])

  const [nodesState, setNodesState] = useState<Node[]>(zoomAdjustedNodes)
  const [edgesState, setEdgesState] = useState<Edge[]>(edges)

  // Update nodes when content data changes (but not while modal is open or drawing)
  React.useEffect(() => {
    if (!showContentViewer && !isDrawing) {
      setNodesState(zoomAdjustedNodes)
    }
  }, [zoomAdjustedNodes, showContentViewer, isDrawing])

  // Update edges when content data changes (but not while modal is open or drawing)
  React.useEffect(() => {
    if (!showContentViewer && !isDrawing) {
      setEdgesState(edges)
    }
  }, [edges, showContentViewer, isDrawing])

  const onNodesChange = useCallback((changes: any) => {
    // Handle node changes if needed
  }, [])

  const onEdgesChange = useCallback((changes: any) => {
    // Handle edge changes if needed
  }, [])

  // Point-in-polygon algorithm for lasso selection
  const isPointInPolygon = useCallback(
    (point: { x: number; y: number }, polygon: { x: number; y: number }[]) => {
      if (polygon.length < 3) return false

      let inside = false
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        if (
          polygon[i].y > point.y !== polygon[j].y > point.y &&
          point.x <
            ((polygon[j].x - polygon[i].x) * (point.y - polygon[i].y)) /
              (polygon[j].y - polygon[i].y) +
              polygon[i].x
        ) {
          inside = !inside
        }
      }
      return inside
    },
    []
  )

  // Optimized lasso selection handler
  const handleLassoComplete = useCallback(() => {
    if (lassoPath.length < 3 || !reactFlowInstance) {
      setLassoPath([])
      setIsDrawing(false)
      return
    }

    // Batch all state updates together
    const newSelectedNodes = new Set<string>()

    // Transform lasso path from screen to ReactFlow coordinates
    const transformedLassoPath = lassoPath.map((point) =>
      reactFlowInstance.screenToFlowPosition({ x: point.x, y: point.y })
    )

    // Process selection in a single pass - check each node against lasso polygon
    nodesState.forEach((node) => {
      const nodeCenter = { x: node.position.x + 4, y: node.position.y + 4 }
      if (isPointInPolygon(nodeCenter, transformedLassoPath)) {
        if (node.type === 'cluster') {
          // For clusters, select all individual nodes within the cluster
          const clusterData = node.data
          if (clusterData.nodeIds && Array.isArray(clusterData.nodeIds)) {
            clusterData.nodeIds.forEach((nodeId: string) => {
              newSelectedNodes.add(nodeId)
            })
            newSelectedNodes.add(node.id)
          }
        } else {
          // For individual nodes, just add them directly
          newSelectedNodes.add(node.id)
        }
      }
    })

    // Batch all state updates
    setSelectedNodes(newSelectedNodes)
    setLassoPath([])
    setIsDrawing(false)
  }, [lassoPath, nodesState, isPointInPolygon, reactFlowInstance])

  // Optimized mouse event handlers for lasso drawing
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || event.button !== 0) return // Only left click

      event.preventDefault()
      event.stopPropagation()

      const x = event.clientX
      const y = event.clientY

      setIsDrawing(true)
      setLassoPath([{ x, y }])
      setSelectedNodes(new Set()) // Clear previous selection
    },
    [lassoMode]
  )

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || !isDrawing) return

      event.preventDefault()
      event.stopPropagation()

      const x = event.clientX
      const y = event.clientY

      // Throttle mouse move updates to improve performance
      setLassoPath((prev) => {
        // Only add point if it's significantly different from the last point
        const lastPoint = prev[prev.length - 1]
        if (lastPoint && Math.abs(lastPoint.x - x) < 3 && Math.abs(lastPoint.y - y) < 3) {
          return prev // Skip this update if the movement is too small
        }
        return [...prev, { x, y }]
      })
    },
    [lassoMode, isDrawing]
  )

  const handleMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || !isDrawing) return

      event.preventDefault()
      event.stopPropagation()

      handleLassoComplete()
    },
    [lassoMode, isDrawing, handleLassoComplete]
  )

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (lassoMode) {
        event.preventDefault() // Prevent context menu in lasso mode
      }
    },
    [lassoMode]
  )

  const isLoading_ = isLoading || isGeneratingPCA || isVectorizing || isLoadingAllContent

  // Helper function to render legend based on visualization mode
  const renderLegend = useCallback(() => {
    switch (visualizationMode) {
      case 'default':
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Default</div>
            <LegendItem>
              <LegendColor color="#28a745" />
              <span>Annotated</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#114ee0" />
              <span>Unannotated</span>
            </LegendItem>
          </Legend>
        )

      case 'annotation':
        const uniqueTags = Array.from(
          new Set(
            (processedContentData?.content || [])
              .filter((item: any) => item.annotation && item.annotation.length > 0)
              .map((item: any) => getMostDominantTag(item.annotation))
              .filter(Boolean)
          )
        )

        const handleTagToggle = (tagId: string) => {
          setVisibleTags((currentVisibleTags) => {
            // If this is the first time using the legend (all tags visible by default)
            if (currentVisibleTags.size === 0) {
              // Initialize with all available tags, then remove the clicked one
              const allTags = new Set<string>()
              uniqueTags.forEach((tag) => {
                if (tag) allTags.add(tag as string)
              })
              allTags.add('no-tags')
              allTags.delete(tagId) // Remove the clicked tag to "hide" it
              return allTags
            }

            // Normal toggle behavior using current state
            const newVisibleTags = new Set(currentVisibleTags)
            if (newVisibleTags.has(tagId)) {
              newVisibleTags.delete(tagId)
            } else {
              newVisibleTags.add(tagId)
            }
            return newVisibleTags
          })
        }

        return (
          <Legend $expanded={expandedLegend}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              By Tag
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 'normal',
                  opacity: 0.8,
                  marginTop: '0.2rem',
                }}
              >
                Click to toggle visibility
              </div>
            </div>
            <LegendItemsContainer $expanded={expandedLegend}>
              {(expandedLegend ? uniqueTags : uniqueTags.slice(0, 8))
                .map((tagId) => {
                  // Show 8 tags or all if expanded
                  if (!tagId) return null
                  const tagInfo = tagIdToNameAndColor[tagId as string]
                  if (!tagInfo) return null
                  // Show as visible only if tag is in visible set (or if no filtering is active)
                  const isVisible = visibleTags.size === 0 || visibleTags.has(tagId as string)
                  return (
                    <LegendItem
                      key={tagId as string}
                      style={{
                        cursor: 'pointer',
                        opacity: isVisible ? 1 : 0.4,
                        transition: 'opacity 0.2s ease',
                        padding: '0.15rem',
                        borderRadius: '0.25rem',
                        marginLeft: '-0.15rem',
                        marginRight: '-0.15rem',
                      }}
                      onClick={() => handleTagToggle(tagId as string)}
                      title={`Click to ${isVisible ? 'hide' : 'show'} nodes with "${
                        tagInfo.name
                      }" tag`}
                    >
                      <LegendColor
                        color={tagInfo.color}
                        style={{
                          opacity: isVisible ? 1 : 0.3,
                          border: isVisible
                            ? '1px solid rgba(255, 255, 255, 0.3)'
                            : '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      />
                      <span
                        style={{
                          textDecoration: isVisible ? 'none' : 'line-through',
                        }}
                      >
                        {tagInfo.name}
                      </span>
                    </LegendItem>
                  )
                })
                .filter(Boolean)}
              <LegendItem
                style={{
                  cursor: 'pointer',
                  opacity: visibleTags.size === 0 || visibleTags.has('no-tags') ? 1 : 0.4,
                  transition: 'opacity 0.2s ease',
                  padding: '0.15rem',
                  borderRadius: '0.25rem',
                  marginLeft: '-0.15rem',
                  marginRight: '-0.15rem',
                }}
                onClick={() => handleTagToggle('no-tags')}
                title={`Click to ${
                  visibleTags.size === 0 || visibleTags.has('no-tags') ? 'hide' : 'show'
                } nodes with no tags`}
              >
                <LegendColor
                  color="#6c757d"
                  style={{
                    opacity: visibleTags.size === 0 || visibleTags.has('no-tags') ? 1 : 0.3,
                    border:
                      visibleTags.size === 0 || visibleTags.has('no-tags')
                        ? '1px solid rgba(255, 255, 255, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                />
                <span
                  style={{
                    textDecoration:
                      visibleTags.size === 0 || visibleTags.has('no-tags')
                        ? 'none'
                        : 'line-through',
                  }}
                >
                  No tags
                </span>
              </LegendItem>
            </LegendItemsContainer>
            {uniqueTags.length > 8 && (
              <div
                style={{
                  fontSize: '0.65rem',
                  opacity: 0.7,
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  marginLeft: '-0.25rem',
                  marginRight: '-0.25rem',
                  transition: 'opacity 0.2s ease, background-color 0.2s ease',
                }}
                onClick={() => setExpandedLegend(!expandedLegend)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1'
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.7'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                title={`Click to ${expandedLegend ? 'collapse' : 'expand'} legend`}
              >
                {expandedLegend
                  ? `▲ Show less (${uniqueTags.length} total)`
                  : `▼ +${uniqueTags.length - 8} more tags`}
              </div>
            )}
          </Legend>
        )

      case 'source':
        return (
          <Legend $expanded={expandedLegend}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>By Source</div>
            {getUniqueSources.length === 0 ? (
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>No source data available</div>
            ) : (
              <>
                <LegendItemsContainer $expanded={expandedLegend}>
                  {(expandedLegend ? getUniqueSources : getUniqueSources.slice(0, 6)).map(
                    (
                      source // Show 6 sources or all if expanded
                    ) => (
                      <LegendItem key={source}>
                        <LegendColor color={getSourceColor(source)} />
                        <span>{source}</span>
                      </LegendItem>
                    )
                  )}
                  <LegendItem>
                    <LegendColor color="#6c757d" />
                    <span>Unknown source</span>
                  </LegendItem>
                </LegendItemsContainer>
                {getUniqueSources.length > 6 && (
                  <div
                    style={{
                      fontSize: '0.65rem',
                      opacity: 0.7,
                      marginTop: '0.25rem',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '0.25rem',
                      marginLeft: '-0.25rem',
                      marginRight: '-0.25rem',
                      transition: 'opacity 0.2s ease, background-color 0.2s ease',
                    }}
                    onClick={() => setExpandedLegend(!expandedLegend)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1'
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.7'
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                    title={`Click to ${expandedLegend ? 'collapse' : 'expand'} legend`}
                  >
                    {expandedLegend
                      ? `▲ Show less (${getUniqueSources.length} total)`
                      : `▼ +${getUniqueSources.length - 6} more sources`}
                  </div>
                )}
              </>
            )}
          </Legend>
        )

      case 'size':
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>By Image Size</div>
            <LegendItem>
              <LegendColor color="#e83e8c" />
              <span>Huge (≥2048px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#6f42c1" />
              <span>Very Large (1024-2047px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#17a2b8" />
              <span>Large (512-1023px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#007bff" />
              <span>Medium (256-511px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#20c997" />
              <span>Small (128-255px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#fd7e14" />
              <span>Tiny (&lt;128px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#6c757d" />
              <span>Unknown</span>
            </LegendItem>
          </Legend>
        )

      case 'outliers':
        const outlierCount = detectOutliers.size
        const totalCount = activeContentData?.content?.length || 0
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Outlier Detection
              <div
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 'normal',
                  opacity: 0.8,
                  marginTop: '0.2rem',
                }}
              >
                {outlierCount} of {totalCount} items flagged
              </div>
            </div>
            <LegendItem>
              <LegendColor color="#dc3545" />
              <span>Outliers ({outlierCount})</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#28a745" />
              <span>Normal ({totalCount - outlierCount})</span>
            </LegendItem>
            <div
              style={{
                fontSize: '0.65rem',
                opacity: 0.8,
                marginTop: '0.5rem',
                lineHeight: '1.3',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Detection Methods:</div>
              <div>• Vector isolation (PCA space)</div>
              <div>• Unusual image dimensions</div>
              <div>• High annotation density</div>
              <div>• Diverse tag combinations</div>
            </div>
          </Legend>
        )

      default:
        return null
    }
  }, [
    visualizationMode,
    processedContentData?.content,
    expandedLegend,
    visibleTags,
    getUniqueSources,
    detectOutliers.size,
    activeContentData?.content?.length,
    getMostDominantTag,
    tagIdToNameAndColor,
    getSourceColor,
  ])

  const handleViewContent = useCallback(() => {
    if (selectedNodes.size === 0) return

    // Save current viewport position and zoom
    if (reactFlowInstance) {
      const viewport = reactFlowInstance.getViewport()
      setSavedViewport(viewport)
    }

    // Show the content viewer with current data (don't refetch to avoid viewport changes)
    setShowContentViewer(true)
  }, [selectedNodes, reactFlowInstance])

  // Cleanup old localStorage data on mount
  useEffect(() => {
    try {
      const keys = Object.keys(localStorage)
      const displacementKeys = keys.filter((key) => key.startsWith('displacement-chart-'))

      // Keep only the last 5 project data entries to prevent bloat
      if (displacementKeys.length > 5) {
        const keysToRemove = displacementKeys.slice(0, displacementKeys.length - 5)
        keysToRemove.forEach((key) => safeLocalStorage.removeItem(key))
      }
    } catch (error) {
      console.warn('Failed to cleanup old displacement chart data:', error)
    }
  }) // Run only on mount

  // Debounced stats update to prevent constant card resizing
  useEffect(() => {
    if (!onStatsUpdate || !processedContentData || isDrawing) return

    const timeoutId = setTimeout(
      () => {
        onStatsUpdate({
          contentData: processedContentData,
          selectedNodes,
          visualizationMode,
          renderMode,
          tagIdToNameAndColor,
          onViewContent: handleViewContent,
          outlierReasons: visualizationMode === 'outliers' ? outlierReasons : undefined,
        })
      },
      isDrawing ? 0 : 100
    ) // Immediate update when not drawing, debounced otherwise

    return () => clearTimeout(timeoutId)
  }, [
    onStatsUpdate,
    processedContentData,
    selectedNodes,
    visualizationMode,
    renderMode,
    tagIdToNameAndColor,
    handleViewContent,
    isDrawing,
    outlierReasons,
  ])

  // Handle node clicks for cluster expansion
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: any) => {
      if (node.type === 'cluster') {
        event.preventDefault()
        event.stopPropagation()
        handleClusterClick(node.id)
      }
    },
    [handleClusterClick]
  )

  // State to preserve viewport when viewing content and track initialization
  const [savedViewport, setSavedViewport] = useState<{
    x: number
    y: number
    zoom: number
  } | null>(null)
  const [hasInitializedView, setHasInitializedView] = useState(false)

  // Ensure fitView happens automatically when nodes are first loaded
  React.useEffect(() => {
    if (
      reactFlowInstance &&
      nodesState.length > 0 &&
      !hasInitializedView &&
      !isDrawing &&
      !showContentViewer
    ) {
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.1, // 10% padding for visual breathing room
          includeHiddenNodes: false,
          minZoom: 0.3,
          maxZoom: 1.5,
          duration: 800, // Smooth centering animation
        })
        setHasInitializedView(true)
      }, 300) // Allow time for nodes to render

      return () => clearTimeout(timer)
    }
  }, [reactFlowInstance, nodesState.length, hasInitializedView, isDrawing, showContentViewer])

  // Track zoom changes for dynamic node spacing
  const onMove = useCallback(
    (event: any, viewport: any) => {
      const newZoom = viewport.zoom
      if (Math.abs(newZoom - currentZoom) > 0.05) {
        // Update when zoom changes by more than 5% for more responsive spacing
        setCurrentZoom(newZoom)
      }
    },
    [currentZoom]
  )

  // Show error if content fetch failed
  if (contentFetchError && !isLoadingAllContent) {
    return (
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>
            <Activity size={20} />
            Content Displacement Chart
          </ChartTitle>
        </ChartHeader>
        <div style={{ padding: '2rem' }}>
          <Alert variant="danger">
            {contentFetchError}
            <div style={{ marginTop: '1rem' }}>
              <Button variant="outline-light" size="sm" onClick={fetchAllContentPages}>
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>
          <Activity size={16} />
          Displacement Chart
          {allContentData && (
            <small style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '0.5rem' }}>
              ({processedContentData?.content?.length || 0} of {allContentData.content?.length || 0}{' '}
              items)
            </small>
          )}
        </ChartTitle>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <GenerateButton onClick={handleGeneratePCA} disabled={isLoading_ || !projectId}>
            {isGeneratingPCA ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {processedContentData ? 'Regenerating PCA...' : 'Generating PCA...'}
              </>
            ) : processedContentData ? (
              'Regenerate PCA'
            ) : (
              'Generate PCA'
            )}
          </GenerateButton>
          <GenerateButton onClick={handleVectorizeProject} disabled={isLoading_ || !projectId}>
            {isVectorizing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Vectorizing...
              </>
            ) : (
              'Vectorize'
            )}
          </GenerateButton>
        </div>
      </ChartHeader>

      {error && (
        <Alert variant="danger" className="m-3 mb-0">
          {error}
        </Alert>
      )}

      {!showChart ? (
        <LoadingContainer>
          <Activity size={48} color="#114ee0" />
          <p>Click "Generate PCA" to create displacement visualization</p>
        </LoadingContainer>
      ) : isLoading_ ? (
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p>Loading displacement data...</p>
        </LoadingContainer>
      ) : (
        <FlowContainer style={{ position: 'relative' }} className={lassoMode ? 'lasso-mode' : ''}>
          <div style={{ position: 'relative', height: '100%' }}>
            {/* Button Overlay */}
            <ButtonOverlay>
              {showChart && (
                <>
                  <div className="button-row">
                    {/* Performance Mode Selector */}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="dropdown-toggle"
                      >
                        ⚡{' '}
                        {renderMode === 'full'
                          ? 'Full'
                          : renderMode === 'sample'
                          ? `Sample (${sampleSize})`
                          : renderMode === 'clustered'
                          ? 'Clustered'
                          : 'Viewport'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          backgroundColor: 'rgba(45, 55, 72, 0.95)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Dropdown.Item
                          onClick={() => setRenderMode('full')}
                          style={{
                            color: renderMode === 'full' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Full Rendering ({allContentData?.content?.length || 0} items)
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setRenderMode('clustered')}
                          style={{
                            color: renderMode === 'clustered' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Clustered View (Smart grouping by proximity & color)
                        </Dropdown.Item>
                        <Dropdown.Divider style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                        <Dropdown.Item
                          onClick={() => {
                            setRenderMode('sample')
                            setSampleSize(1000)
                          }}
                          style={{
                            color:
                              renderMode === 'sample' && sampleSize === 1000
                                ? '#fbbf24'
                                : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Sample: 1,000 items
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setRenderMode('sample')
                            setSampleSize(2000)
                          }}
                          style={{
                            color:
                              renderMode === 'sample' && sampleSize === 2000
                                ? '#fbbf24'
                                : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Sample: 2,000 items
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setRenderMode('sample')
                            setSampleSize(3000)
                          }}
                          style={{
                            color:
                              renderMode === 'sample' && sampleSize === 3000
                                ? '#fbbf24'
                                : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Sample: 3,000 items
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setRenderMode('sample')
                            setSampleSize(5000)
                          }}
                          style={{
                            color:
                              renderMode === 'sample' && sampleSize === 5000
                                ? '#fbbf24'
                                : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Sample: 5,000 items
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Visualization Mode Selector */}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="dropdown-toggle"
                      >
                        <Palette size={14} className="me-1" />
                        {visualizationMode === 'default' && 'Default'}
                        {visualizationMode === 'annotation' && 'By Tag'}
                        {visualizationMode === 'source' && 'By Source'}
                        {visualizationMode === 'size' && 'By Size'}
                        {visualizationMode === 'outliers' && 'Outliers'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          backgroundColor: 'rgba(45, 55, 72, 0.95)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Dropdown.Item
                          onClick={() => setVisualizationMode('default')}
                          style={{
                            color: visualizationMode === 'default' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          <Activity size={14} className="me-2" />
                          Default (Annotated/Unannotated)
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setVisualizationMode('annotation')}
                          style={{
                            color: visualizationMode === 'annotation' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          <Filter size={14} className="me-2" />
                          By Most Dominant Tag
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setVisualizationMode('source')}
                          style={{
                            color: visualizationMode === 'source' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          <Eye size={14} className="me-2" />
                          By Data Source
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setVisualizationMode('size')}
                          style={{
                            color: visualizationMode === 'size' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          <X size={14} className="me-2" />
                          By Image Size
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setVisualizationMode('outliers')}
                          style={{
                            color: visualizationMode === 'outliers' ? '#fbbf24' : '#ffffff',
                            backgroundColor: 'transparent',
                          }}
                        >
                          <Activity size={14} className="me-2" />
                          Outlier Detection
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <div className="button-row">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowMiniMap(!showMiniMap)}
                      className="btn"
                    >
                      {showMiniMap ? 'Hide Map' : 'Show Map'}
                    </Button>

                    <Button
                      variant={lassoMode ? 'warning' : 'outline-secondary'}
                      size="sm"
                      onClick={() => {
                        setLassoMode(!lassoMode)
                        setSelectedNodes(new Set())
                        setLassoPath([])
                        setIsDrawing(false)
                      }}
                      className="btn"
                    >
                      {lassoMode ? 'Exit Lasso' : 'Lasso Select'}
                    </Button>

                    {/* Reset Clusters Button - only show when in clustered mode and clusters are expanded */}
                    {renderMode === 'clustered' && isClusterExpanded && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={resetClusterExpansions}
                        className="btn"
                        title="Reset all expanded clusters"
                      >
                        Reset Clusters
                      </Button>
                    )}
                  </div>
                </>
              )}
            </ButtonOverlay>

            {/* Legend */}
            {renderLegend()}

            {/* Lasso drawing overlay - only blocks when actively drawing */}
            {lassoMode && isDrawing && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1000,
                  cursor: 'crosshair',
                  pointerEvents: 'auto',
                  background: 'transparent',
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onContextMenu={handleContextMenu}
              />
            )}

            {/* Optimized lasso path visualization */}
            {lassoMode && lassoPath.length > 1 && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 999,
                }}
              >
                <path
                  d={`M ${lassoPath
                    .map((p) => {
                      const containerElement = document.querySelector('.react-flow') as HTMLElement
                      if (!containerElement) return `${p.x},${p.y}`
                      const rect = containerElement.getBoundingClientRect()
                      const containerX = p.x - rect.left
                      const containerY = p.y - rect.top
                      return `${containerX},${containerY}`
                    })
                    .join(' L ')} ${isDrawing ? '' : 'Z'}`}
                  stroke="#fbbf24"
                  strokeWidth="2"
                  fill={isDrawing ? 'none' : 'rgba(251, 191, 36, 0.1)'}
                  strokeDasharray="5,5"
                />
              </svg>
            )}

            <div
              onMouseDown={(e) => {
                if (lassoMode && e.button === 0) {
                  // Left mouse button in lasso mode
                  handleMouseDown(e)
                }
              }}
              style={{
                height: '100%',
                width: '100%',
                cursor: lassoMode ? 'crosshair' : 'default',
              }}
            >
              <ReactFlow
                nodes={nodesState}
                edges={edgesState}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                onInit={(instance) => {
                  setReactFlowInstance(instance)
                }}
                attributionPosition="bottom-left"
                proOptions={{ hideAttribution: true }}
                panOnDrag={lassoMode ? [1] : true} // Allow middle mouse panning, disable left drag in lasso mode
                zoomOnScroll={true} // Always allow scroll wheel zoom
                zoomOnPinch={true} // Always allow pinch zoom
                zoomOnDoubleClick={!lassoMode} // Disable double-click zoom when in lasso mode
                onNodeClick={onNodeClick}
                minZoom={0.3} // More reasonable minimum zoom
                maxZoom={4} // More reasonable maximum zoom
                defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                onMove={onMove}
                style={{
                  cursor: lassoMode ? 'crosshair !important' : 'default',
                }}
              >
                <Background color="#333" gap={20} />
                <Controls />
                {showMiniMap && (
                  <MiniMap
                    nodeColor={(node) => node.data.color}
                    nodeStrokeWidth={3}
                    zoomable
                    pannable
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                    }}
                  />
                )}
              </ReactFlow>
            </div>
          </div>
        </FlowContainer>
      )}

      {/* Content Viewer Modal */}
      <ContentViewerModal
        show={showContentViewer}
        onHide={() => {
          setShowContentViewer(false)
          // Update nodes/edges after modal closes, then restore viewport
          setTimeout(() => {
            setNodesState(zoomAdjustedNodes)
            setEdgesState(edges)
            // Restore viewport after nodes are updated
            if (reactFlowInstance && savedViewport) {
              setTimeout(() => {
                reactFlowInstance.setViewport(savedViewport)
              }, 50)
            }
          }, 50)
        }}
        size="xl"
        centered
      >
        <Modal.Header>
          <Modal.Title>
            <Eye size={20} className="me-2" />
            Selected Content ({selectedContentItems.length} items)
          </Modal.Title>
          <Button
            variant="link"
            onClick={() => {
              setShowContentViewer(false)
              // Update nodes/edges after modal closes, then restore viewport
              setTimeout(() => {
                setNodesState(zoomAdjustedNodes)
                setEdgesState(edges)
                // Restore viewport after nodes are updated
                if (reactFlowInstance && savedViewport) {
                  setTimeout(() => {
                    reactFlowInstance.setViewport(savedViewport)
                  }, 50)
                }
              }, 50)
            }}
            style={{ color: '#ffffff', textDecoration: 'none' }}
          >
            <X size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          {selectedContentItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#adb5bd' }}>
              No content selected. Use the lasso tool to select nodes first.
            </div>
          ) : (
            <ContentGrid>
              {selectedContentItems.map((item, index) => (
                <ContentItem key={item.id || index}>
                  {item.b64_image && (
                    <ContentImage
                      src={`data:${item.content_type || 'image/jpeg'};base64,${item.b64_image}`}
                      alt={item.name || `Content ${index + 1}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  )}
                  <ContentInfo>
                    <div className="content-name">{item.name || `Content ${index + 1}`}</div>
                    <div className="content-details">
                      {item.width && item.height && (
                        <div>
                          {item.width} × {item.height}
                        </div>
                      )}
                    </div>
                    <div className="annotation-status">
                      {item.annotation && item.annotation.length > 0 ? (
                        <div>
                          {item.annotation.map((ann: any, annIndex: number) => (
                            <div key={annIndex} style={{ marginBottom: '0.25rem' }}>
                              {renderTagBadges(ann.tagids)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                          }}
                        >
                          No tags
                        </span>
                      )}
                    </div>
                  </ContentInfo>
                </ContentItem>
              ))}
            </ContentGrid>
          )}
        </Modal.Body>
      </ContentViewerModal>
    </ChartContainer>
  )
}

export default DisplacementChart
